import './carrier-form-infos.scss';
import { useCallback, useEffect, useState } from 'react';
import { ZodFormattedError } from 'zod';
import { ClbCheckbox } from '@celebration/react/ClbCheckbox/ClbCheckbox';
import { useTranslation } from 'react-i18next';
import Shape from '../../../../../../common/components/shape';
import ICarrierFormInfosProps from './interfaces/ICarrierFormInfosProps';
import Input from '../../../../../../common/components/input';
import placeholderPatterns from '../../../../../../common/components/input/placeholder-patterns';
import {
    MASKTYPE,
    TYPE,
} from '../../../../../../common/components/input/input-constants';
import { phoneMask } from '../../../../../../utils/masks-constants';
import IContact, {
    ContactType,
} from '../../../../../../common/interfaces/IContacts';
import { IContactErrors } from '../../../../interfaces/ICarrierFormDataErrors';
import { EventType } from '../../interfaces/IHandleCarrierFormChangeProps';
import ValidationService from '../../../../../../common/services/validation';
import SelectAutocomplete from '../../../../../../common/components/select-autocomplete';

function CarrierFormInfos({
    handleCarrierFormChange,
    carrierFormData,
    inputErrors,
    isEditForm,
}: Readonly<ICarrierFormInfosProps>) {
    const { t } = useTranslation();
    const carrierDataTitle = t('DadoTransportadora');
    const basicDataSubTitle = t('DadosBasicos');
    const contactSubTitle = t('Contato');
    const [errorsOnExistingData, setErrorsOnExistingData] = useState<
        boolean | undefined
    >(undefined);

    useEffect(() => {
        if (carrierFormData?.id && errorsOnExistingData === undefined) {
            setErrorsOnExistingData(!inputErrors?._errors);
        }
    }, [carrierFormData?.id, inputErrors, errorsOnExistingData]);

    const handleCheckbox = useCallback(
        (event: Event) => {
            const { checked } = event?.target as HTMLInputElement;
            if (checked) {
                handleCarrierFormChange({
                    property: 'legacy',
                    newValue: true,
                });
            } else {
                handleCarrierFormChange({
                    property: 'legacy',
                    newValue: false,
                });
            }
        },
        [handleCarrierFormChange],
    );

    const renderCarrierData = () => {
        return (
            <>
                <h2> {basicDataSubTitle} </h2>
                <div className="carrier-data-div">
                    <Input
                        id="trade-name"
                        label={t('Nome')}
                        value={carrierFormData?.tradeName}
                        className="trade-name"
                        placeholder={t('TransportadoraInserirNome')}
                        onChange={(e: any) =>
                            handleCarrierFormChange({
                                property: 'tradeName',
                                newValue: e.target.value,
                            })
                        }
                        error={!!inputErrors?.tradeName}
                        helperText={ValidationService.getError(
                            inputErrors?.tradeName,
                            t,
                        )}
                        dataTestId="trade-name"
                        disabled={isEditForm}
                    />
                    <Input
                        id="paymentTerm"
                        mask="decimal_small"
                        label={t('TransportadoraPrazoPagamento')}
                        placeholder={placeholderPatterns.DECIMAL_SMALL}
                        value={carrierFormData?.paymentTerm}
                        onChange={(e: any) =>
                            handleCarrierFormChange({
                                property: 'paymentTerm',
                                newValue: e.target.value,
                            })
                        }
                        error={!!inputErrors?.paymentTerm}
                        helperText={ValidationService.getError(
                            inputErrors?.paymentTerm,
                            t,
                        )}
                        dataTestId="payment-term"
                    />

                    {!isEditForm && (
                        <ClbCheckbox
                            name="Checkbox"
                            data-testid="checkbox"
                            checked={carrierFormData?.legacy}
                            onChange={(e: any) => handleCheckbox(e)}
                        >
                            {t('TransportadoraCadastroEgates')}
                        </ClbCheckbox>
                    )}

                    <div className="margin-id-egates">
                        <Input
                            id="id-egates"
                            label="ID Egates"
                            type={TYPE.NUMBER}
                            placeholder={placeholderPatterns.ID_EGATES}
                            maxlength={4}
                            value={carrierFormData?.id}
                            hidden={!carrierFormData?.legacy}
                            onChange={(e: any) =>
                                handleCarrierFormChange({
                                    property: 'id',
                                    newValue: e.target.value,
                                })
                            }
                            counterEnabled
                            error={!!inputErrors?.id}
                            helperText={ValidationService.getError(
                                inputErrors?.id,
                                t,
                            )}
                            dataTestId="id-egates"
                        />
                    </div>
                </div>
            </>
        );
    };

    const renderCarrierContact = (
        contactEventType: EventType,
        contact?: IContact,
        contactErrors?: ZodFormattedError<IContactErrors, string>,
    ) => {
        return (
            <>
                <h2> {contactSubTitle} </h2>
                <div className="width-email">
                    <Input
                        id="carrierMail"
                        label={t('Email')}
                        placeholder={placeholderPatterns.EMAIL}
                        value={contact?.email}
                        onChange={(e: any) =>
                            handleCarrierFormChange({
                                property: 'email',
                                newValue: e.target.value,
                                eventType: contactEventType,
                            })
                        }
                        error={!!contactErrors?.email}
                        helperText={ValidationService.getError(
                            contactErrors?.email,
                            t,
                        )}
                        dataTestId="contact-mail"
                    />
                </div>

                <div className="contact-div-flex">
                    <div className="margin-phone">
                        <Input
                            id="carrierPhone"
                            label={t('Telefone')}
                            placeholder={placeholderPatterns.PHONE}
                            mask={MASKTYPE.PHONE}
                            value={contact?.phone}
                            onChange={(e: any) =>
                                handleCarrierFormChange({
                                    property: 'phone',
                                    newValue: phoneMask(e.target.value),
                                    eventType: contactEventType,
                                })
                            }
                            error={!!contactErrors?.phone}
                            helperText={ValidationService.getError(
                                contactErrors?.phone,
                                t,
                            )}
                            dataTestId="contact-phone"
                        />
                    </div>

                    <div className="contact-div-select">
                        <SelectAutocomplete
                            key={0}
                            label={t('TipoTelefone')}
                            placeholder="Select"
                            onChange={(e: any) =>
                                handleCarrierFormChange({
                                    property: 'contactType',
                                    newValue: e,
                                    eventType: contactEventType,
                                })
                            }
                            dataTestId="phone-type"
                            value={contact?.contactType}
                            defaultValue={ContactType.operational}
                            options={[
                                {
                                    label: t('TipoTelefoneOperacional'),
                                    value: ContactType.operational,
                                },
                                {
                                    label: t('TipoTelefoneComercial'),
                                    value: ContactType.commercial,
                                },
                            ]}
                        />
                    </div>

                    <div>
                        <Input
                            id="CarrierResponsible"
                            label={t('Responsavel')}
                            placeholder={t('ResponsavelInserir')}
                            value={contact?.responsible}
                            onChange={(e: any) =>
                                handleCarrierFormChange({
                                    property: 'responsible',
                                    newValue: e.target.value,
                                    eventType: contactEventType,
                                })
                            }
                            error={!!contactErrors?.responsible}
                            helperText={ValidationService.getError(
                                contactErrors?.responsible,
                                t,
                            )}
                            dataTestId="contact-responsible"
                        />
                    </div>
                    <div>
                        <Input
                            id="CarrierOccupation"
                            label={t('Funcao')}
                            placeholder={t('FuncaoInserir')}
                            value={contact?.occupation}
                            onChange={(e: any) =>
                                handleCarrierFormChange({
                                    property: 'occupation',
                                    newValue: e.target.value,
                                    eventType: contactEventType,
                                })
                            }
                            error={!!contactErrors?.occupation}
                            helperText={ValidationService.getError(
                                contactErrors?.occupation,
                                t,
                            )}
                            dataTestId="contact-responsible-function"
                        />
                    </div>
                </div>
            </>
        );
    };
    return (
        <Shape>
            <h1> {carrierDataTitle} </h1>
            {renderCarrierData()}
            {renderCarrierContact(
                EventType.ContactChange,
                carrierFormData?.carriersContact,
                inputErrors?.carriersContact,
            )}
        </Shape>
    );
}

export default CarrierFormInfos;
